import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';

import Category from '../../../models/Category';
import InfoPage from '../../../models/InfoPage';
import FullProduct from '../../../models/product/FullProduct';
import Section from '../../../models/Section';
import { modelOf } from '../../../prop-types';
import RouteService, { X_DEFAULT } from '../../../services/RouteService';
import LanguageStore from '../../../store/LanguageStore';
import SEOLink from '../SEOLink';
import SectionStore from "../../../store/SectionStore";
import ProductClass from "../../../types/ProductClass";
import ConfigStore from "../../../store/ConfigStore";

const AlternateLink = ({
  configStore,
  languageStore,
  sectionStore,
  routeService,
  model,
  isCategoryPage,
  isProductPage,
  isSectionPage,
  isInfoPage,
  path,
}) => {
  const languages = languageStore.languages;
  if (languages.length <= 1) {
    return null;
  }

  const renderAlternateLinks = () => {
    return languages.map((language) => {
      return isTranslated(language.code) ? (
        <SEOLink
          key={language.code}
          rel="alternate"
          path={getPath(language.code)}
          hreflang={language.code}
        />
      ) : null;
    });
  };

  const isTranslated = (languageCode) => {
    // Content pages should not be shown when untranslated.
    if (isInfoPage && model) {
      return model.hasLocalizedPath(languageCode) || languageStore.activeLocale === languageCode;
    }
    return true;
  }

  const getPath = (languageCode) => {
    const currentLocal = languageCode === X_DEFAULT ? configStore.defaultLocale : languageCode;

    if (isCategoryPage && model) {
      return routeService.getCanonicalCategoryPath(
        model,
        model.getLocalizedPath(currentLocal),
        languageCode
      );
    }

    if (isProductPage && model) {
      return routeService.getCanonicalProductPath(
        model,
        model.getLocalizedPath(
          currentLocal,
          model.class === ProductClass.MULTI_CHILD ? model.id : null
        ),
        languageCode
      );
    }

    if (isSectionPage && model) {
      return routeService.getPath(
        model.getLocalizedPath(currentLocal),
        null,
        languageCode
      );
    }

    if (isInfoPage && model) {
      return routeService.getPath(
        model.getLocalizedPath(currentLocal),
        null,
        languageCode
      );
    }
    return routeService.getPath(
      path,
      isSectionPage ? sectionStore.activeSection : null,
      languageCode
    );
  };

  return (
    <>
      {renderAlternateLinks()}
      <SEOLink
        rel="alternate"
        path={getPath(X_DEFAULT)}
        hreflang="x-default"
      />
    </>
  )
};

AlternateLink.propTypes = {
  configStore: modelOf(ConfigStore).isRequired,
  languageStore: modelOf(LanguageStore).isRequired,
  sectionStore: modelOf(SectionStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  model: PropTypes.oneOfType([
    modelOf(Category),
    modelOf(FullProduct),
    modelOf(Section),
    modelOf(InfoPage),
    PropTypes.object,
  ]),
  isCategoryPage: PropTypes.bool,
  isProductPage: PropTypes.bool,
  isSectionPage: PropTypes.bool,
  isInfoPage: PropTypes.bool,
  path: PropTypes.string,
};

export default inject(
  'configStore',
  'languageStore',
  'sectionStore',
  'routeService'
)(observer(AlternateLink));
